<template>
  <div id="app">
    <router-view/>
    <center class="center"/>
  </div>
</template>

<script>
import center from '@/views/center'

export default {
  components: {
    center
  },

  data() {
    return {
      lockKey: 'zikao-single-window-lock',
      isFirstWindow: false,
      lockRefreshTimer: null,
      heartbeatTimer: null,
      lastLockValue: null // Track our own lock value
    };
  },

  mounted() {
    this.$root.getUsreInfo()
    this.initSingleWindowCheck();

    // Add visibility change listener
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },

  beforeDestroy() {
    this.cleanupResources();
  },

  methods: {
    /**
     * Initialize single window detection logic
     */
    initSingleWindowCheck() {
      const existingLock = localStorage.getItem(this.lockKey);
      const currentTime = Date.now();

      // Check if there's a valid lock (extended to 30 seconds to handle browser throttling)
      if (existingLock && (currentTime - parseInt(existingLock) <= 30000)) {
        // Duplicate window detected
        this.handleDuplicateWindow();
      } else {
        // No valid lock, current window acquires the lock
        this.isFirstWindow = true;
        this.updateLock();

        // Small delay before starting timers to ensure lock is properly set
        setTimeout(() => {
          // Start lock refresh timer (every 2 seconds)
          this.lockRefreshTimer = setInterval(() => {
            if (this.isFirstWindow) {
              this.updateLock();
            }
          }, 2000);

          // Start heartbeat check (every 100ms to quickly detect loss of lock)
          this.heartbeatTimer = setInterval(() => {
            this.checkLockValidity();
          }, 100);

          // Listen for page unload to release lock
          window.addEventListener('beforeunload', this.handlePageUnload);
        }, 50);
      }
    },

    /**
     * Handle visibility change events
     */
    handleVisibilityChange() {
      console.log('Visibility change', document.hidden, this.isFirstWindow);
      if (document.hidden) {
        // Window is hidden (minimized or in background)
        // Clear timers to reduce resource usage during background state
        if (this.lockRefreshTimer) {
          clearInterval(this.lockRefreshTimer);
          this.lockRefreshTimer = null;
        }
        if (this.heartbeatTimer) {
          clearInterval(this.heartbeatTimer);
          this.heartbeatTimer = null;
        }
      } else {
        // Window is visible (back to foreground)
        // Restart timers and refresh lock immediately
        if (this.isFirstWindow) {
          this.updateLock(); // Refresh lock immediately when returning to foreground

          // Restart timers
          this.lockRefreshTimer = setInterval(() => {
            if (this.isFirstWindow) {
              this.updateLock();
            }
          }, 2000);

          this.heartbeatTimer = setInterval(() => {
            this.checkLockValidity();
          }, 100);
        }

        // Verify the lock is still valid
        this.checkLockValidity();
      }
    },

    /**
     * Update lock with current timestamp
     */
    updateLock() {
      this.lastLockValue = Date.now().toString();
      localStorage.setItem(this.lockKey, this.lastLockValue);
    },

    /**
     * Check if current window still holds valid lock
     */
    checkLockValidity() {
      if (!this.isFirstWindow) return;

      const existingLock = localStorage.getItem(this.lockKey);
      const currentTime = Date.now();
      // console.log('Check lock', existingLock, currentTime);

      // Check if lock exists and is still valid (within 30 seconds)
      const isValidLock = existingLock && (currentTime - parseInt(existingLock) <= 30000);

      // If no valid lock exists, this means another window took it or it expired
      if (!isValidLock) {
        this.handleLockLost();
        return;
      }

      // Additional check: if the lock value is not what we expect, another window might have taken over
      if (existingLock !== this.lastLockValue) {
        this.handleLockLost();
      }
    },

    /**
     * Handle duplicate window scenario
     */
    handleDuplicateWindow() {
      // Redirect to login page and attempt to close window
      this.$router.push('/login');

      // Try to close the window (works for windows opened via window.open())
      setTimeout(() => {
        window.close();

        // Fallback: Show persistent message if window couldn't be closed
        if (!window.closed) {
          this.showPersistentWarning();
        }
      }, 100);
    },

    /**
     * Handle lock loss (another window took over)
     */
    handleLockLost() {
      this.isFirstWindow = false;
      this.$router.push('/login');

      setTimeout(() => {
        window.close();
        if (!window.closed) {
          this.showPersistentWarning();
        }
      }, 100);
    },

    /**
     * Show persistent warning if window cannot be closed
     */
    showPersistentWarning() {
      const warningDiv = document.createElement('div');
      warningDiv.innerHTML = `
        <div style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.8);
          z-index: 999999;
          display: flex;
          justify-content: center;
          align-items: center;
          color: white;
          font-size: 18px;
          text-align: center;
        ">
          <div>
            <p>此应用程序只能在一个浏览器选项卡中前台运行。</p>
            <p>请勿后台挂机或关闭其他窗口后再试。</p>
            <button onclick="location.reload()" style="
              margin-top: 20px;
              padding: 8px 20px;
              font-size: 16px;
              background: #007bff;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
            ">重  试</button>
          </div>
        </div>
      `;
      document.body.appendChild(warningDiv);
    },

    /**
     * Release lock when page unloads
     */
    handlePageUnload() {
      console.log('Page unload', this.isFirstWindow);
      if (this.isFirstWindow) {
        localStorage.removeItem(this.lockKey);
      }
    },

    /**
     * Clean up resources (timers and event listeners)
     */
    cleanupResources() {
      console.log('Cleanup resources====');
      if (this.lockRefreshTimer) {
        clearInterval(this.lockRefreshTimer);
      }
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer);
      }
      window.removeEventListener('beforeunload', this.handlePageUnload);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }
}
</script>

<style lang="scss" scoped>
#app {
  background-color: #fff;
}

#app {
  width: 100vw;
  height: 100vh;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  background: url('@/static/images/ditu01.png') no-repeat;
  background-size: 100% 100%;
  background-attachment: fixed;
}
</style>

<style>
html,
body,
ul,
li {
  margin: 0;
  padding: 0;
}

::-webkit-scrollbar {
  width: 0;
  background: transparent;
}

.scrollbox,
.scroll-box {
  scrollbar-width: none;
}

/* Mobile styles */
@media screen and (max-width: 750px) {
  .center {
    display: none;
  }
}
</style>
